import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Box,
  Divider,
  FormControl,
  Button,
  Chip,
  Stack,
  Container,
  Typography
} from '@mui/material';
import Grid from "@mui/material/Grid2";
import { DataGrid } from '@mui/x-data-grid';
import api from '../../../services/api';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Autocomplete, Datas, AprovacoesCheckbox } from '../../../components';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CSVDownload } from 'react-csv';
import { tagsRel } from './static';
import { removerQuebrasDeLinha } from '../../../utils/removerQuebraDeLinha';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

const meses = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez'
];

const columns = [
  [
    {
      field: 'mes',
      headerName: 'Mês',
      valueGetter: (value) => meses[value - 1],
      type: 'string',
      width: 30,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: 'totalAtestaCid',
      headerName: 'Número de novos atestados em CID F',
      editable: true,
      type: 'number',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      description:
        'CID F – Classificação Internacional ' +
        'de Doenças, estabelecida pela OMS, ' +
        'cuja categoria diagnóstica identifica um ' +
        'transtorno mental ou de comportamento.'
    },
    {
      field: 'totalDiasPerdCid',
      headerName: 'Número de dias perdidos por CID F',
      editable: true,
      type: 'number',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      description:
        'CID F – Classificação Internacional ' +
        'de Doenças, estabelecida pela OMS, ' +
        'cuja categoria diagnóstica identifica um ' +
        'transtorno mental ou de comportamento.'
    },
    {
      field: 'empregadosCid',
      headerName: 'Número de empregados próprios que apresentam CID F',
      editable: true,
      type: 'number',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      description:
        'CID F – Classificação Internacional ' +
        'de Doenças, estabelecida pela OMS, ' +
        'cuja categoria diagnóstica identifica um ' +
        'transtorno mental ou de comportamento.'
    },
    {
      field: 'absDiasPerd',
      headerName: 'Absenteísmo: Dias perdidos',
      editable: true,
      type: 'number',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      description:
        'A soma de dias perdidos corridos por ausência ao trabalho ' +
        'por motivo de saúde, considerando todos os empregados ' +
        'próprios afastados no período de referência, ' +
        'independentemente deste número de dias ser maior ou menor ' +
        'que 15. Incluindo faltas por consultas eletivas ' +
        'relacionadas a doenças.Excluindo licenças legais e ' +
        'maternidade.'
    },
    {
      field: 'createdAt',
      headerName: 'Inserido',
      type: 'dateTime',
      valueGetter: (value) => value && new Date(value),
      width: 157,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: 'updatedAt',
      headerName: 'Atualizado',
      type: 'dateTime',
      valueGetter: (value) => value && new Date(value),
      width: 157,
      sortable: false,
      disableColumnMenu: true
    }
  ]
];

const Tema03b = () => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);
  let user = useStoreState(state => state.auth.user);

  const [empresas, setEmpresas] = React.useState([]);
  const [cnaes, setCNAEs] = React.useState([]);
  const [registros, setRegistros] = React.useState([]);
  const [anoSelecionado, setAnoSelecionado] = React.useState(
    new Date().getFullYear()
  );
  const [open, setOpen] = React.useState(false);
  const [preenchido, setPreenchido] = React.useState(null);
  const [csvData, setCsvData] = React.useState([]);

  const schema = yup
    .object({
      empresa: yup
        .number()
        .positive()
        .nullable()
        .label('Selecione a empresa')
        .required(),
      cnae: yup
        .number()
        .positive()
        .nullable()
        .label('Selecione o CNAE')
        .required()
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const { watch, setValue } = useFormRef;

  const loadEmpresas = async mount => {
    startLoading();
    try {
      let result = (
        await api.http.get(
          '/empresas/' + (user.roleId == 'admin' ? '?all=1' : '')
        )
      ).data;
      if (mount.isMounted) setEmpresas(result);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const loadEmpresaCNAEs = async mount => {
    setValue('cnae', null);
    if (!empresaId) {
      setCNAEs([]);
      return;
    }
    startLoading();
    try {
      let result = (await api.http.get('/empresas/' + empresaId + '/cnaes'))
        .data;
      if (mount.isMounted) setCNAEs(result);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const empresaId = watch('empresa');
  const cnaeId = watch('cnae');

  const submit = data => {
    loadRegistros(data.empresa, data.cnae);
  };

  const onChangeAno = (event, item) => {
    setAnoSelecionado(item);
  };

  const loadRegistros = async mount => {
    startLoading();
    try {
      let url =
        '03b/registros/empresa/' +
        empresaId +
        '/cnae/' +
        cnaeId +
        '/' +
        anoSelecionado;
      let getRegistros = (await api.http.get(url)).data;
      let newRegistros = [];
      let currReg = 0;
      for (let mes = 0; mes < 12; mes++) {
        if (
          getRegistros.length > currReg &&
          getRegistros[currReg].mes == mes + 1
        ) {
          newRegistros.push(getRegistros[currReg++]);
        } else {
          newRegistros.push({
            ano: anoSelecionado,
            mes: mes + 1,
            empresaId: empresaId,
            cnaeId: cnaeId
          });
        }
      }
      if (mount.isMounted) setRegistros(newRegistros);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  const processRowUpdate = React.useCallback(async (newRow, oldRow) => {
    startLoading();

    try {
      let result;
      if (newRow.id) {
        result = (
          await api.http.put(
            '03b/registros/empresa/' + newRow.empresaId + '/' + newRow.id,
            newRow
          )
        ).data;
      } else {
        result = (
          await api.http.post(
            '03b/registros/empresa/' +
              oldRow.empresaId +
              '/cnae/' +
              oldRow.cnaeId +
              '/' +
              oldRow.ano,
            newRow
          )
        ).data;
      }
      setReloadOnTabChange(true);
      stopLoading();
      return result;
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      return oldRow;
    }
  }, []);

  const loadPreenchido = async () => {
    startLoading();
    try {
      let result = (
        await api.http.get(
          `03b/registros-preenchidos/empresa/${empresaId}/cnae/${cnaeId}/${anoSelecionado}`
        )
      ).data;
      setPreenchido(result);
      if (result.length == 0) {
        setPreenchido(null);
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadEmpresas(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadEmpresaCNAEs(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [empresaId]);

  React.useEffect(() => {
    if (!empresaId || !cnaeId) return;
    let mount = {
      isMounted: true
    };
    loadRegistros(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [empresaId, cnaeId, anoSelecionado]);

  const [reloadOnTabChange, setReloadOnTabChange] = React.useState(false);
  const [tab, setTab] = React.useState(0);

  // abre o dialogo de finalizar e salvar
  const handleClickOpen = () => {
    loadPreenchido();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const exportDados = async () => {
    startLoading();
    try {
      const dados = (await api.http.get('/03b/registros')).data;
      const dadosCsv = dados.map(data => {
        return {
          cnpj: data?.empresa?.cnpj,
          empresa: removerQuebrasDeLinha(data?.empresa?.nome),
          ano: data?.ano,
          mes: meses[data?.mes - 1],
          cnae: `${data?.cnae?.codigo} - ${data?.cnae?.descr}`,
          totalAtestaCid: data?.totalAtestaCid,
          totalDiasPerdCid: data?.totalDiasPerdCid,
          empregadosCid: data?.empregadosCid,
          absDiasPerd: data?.absDiasPerd,
          createdAt: data.createdAt ? extrairDataHora(data.createdAt) : null,
          updatedAt: data.updatedAt ? extrairDataHora(data.updatedAt) : null
        };
      });

      if (dadosCsv.length == 0) {
        stopLoading({
          message: 'Nenhum registro encontrado.',
          severity: 'error'
        });
      } else {
        setCsvData([tagsRel, ...dadosCsv]);
        stopLoading();
      }
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };
  function extrairDataHora(dateTimeStr) {
    const dateObj = new Date(dateTimeStr);
    const year = dateObj.getFullYear().toString();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const date = `${day}-${month}-${year}`;
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const seconds = dateObj.getSeconds().toString().padStart(2, '0');
    const time = `${hours}:${minutes}:${seconds}`;
    return date + ' ' + time;
  }
  return (
    (<Container maxWidth="false">
      <Paper
        variant="outlined"
        className="center-box"
        sx={{
          textAlign: 'center',
          px: 4,
          pt: 2,
          pb: 3,
          m: 5
        }}
      >
        <Grid m={3}>
          <Typography>
            <h3>O preenchimento do GT 3 deve ser feito diretamente na plataforma:</h3>
            <br></br>
            <a
              href="http://bit.ly/ibramsso"
              target="_blank"
              style={{
                fontWeight: 'bold',
                color: '#004071',
                marginLeft: 5
              }}
              rel="noreferrer"
            >
              Preenchimento ESG da Mineração - GT 03a e 03b
            </a>
            <br></br>
            Período de referência: as empresas devem ter atualizado os dados até o 1º semestre/2025
            <br></br>
            Prazo para preenchimento: até 15 de agosto de 2025
          </Typography>
        </Grid>
      </Paper>
    </Container>)
  );
};

export default Tema03b;
